import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

/**
 * Modal — v2 dialog primitive.
 *
 * Sticky header + scrollable body + sticky footer (when supplied).
 * Closes on Escape. Backdrop click closes by default — pass
 * `dismissOnBackdrop={false}` for irreversible-confirm modals
 * (kill switch, deletion of published assets) where accidental
 * dismissal would be costly.
 *
 * Accessibility:
 *   - role="dialog", aria-modal="true"
 *   - Focusable close button always present
 *   - Initial focus moves into the dialog body on open
 *   - Body scroll is locked while the modal is open
 *
 * Composition:
 *   <Modal isOpen={open} onClose={close} title="Confirm" footer={
 *     <>
 *       <Button variant="secondary" onClick={close}>Cancel</Button>
 *       <Button variant="danger" onClick={confirm}>Delete</Button>
 *     </>
 *   }>
 *     <p>Are you sure?</p>
 *   </Modal>
 *
 * @param {Object} props
 * @param {boolean}            props.isOpen
 * @param {() => void}         props.onClose
 * @param {React.ReactNode}    [props.title]
 * @param {React.ReactNode}    props.children
 * @param {React.ReactNode}    [props.footer]
 * @param {'sm'|'md'|'lg'|'full'} [props.size='md']
 * @param {boolean}            [props.dismissOnBackdrop=true]
 * @param {string}             [props.ariaLabel] — required if title is non-string
 */
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  dismissOnBackdrop = true,
  ariaLabel,
}) {
  const dialogRef = useRef(null);

  /* Hold `onClose` in a ref so the focus + key-binding effect below
   * does NOT re-fire every time the parent passes a fresh closure.
   *
   * Why this matters: every caller writes `onClose={() => setX(null)}`
   * (a new function reference on every render). With `onClose` in the
   * effect's dependency array, the effect's body was firing on every
   * parent re-render — including the renders triggered by typing into
   * an input inside the modal (type-to-confirm Delete, etc.). The
   * initial-focus query inside the effect would then steal focus to
   * the first matching focusable, which — because of CSS comma-OR
   * matching evaluating in DOM order — was the close-X button (above
   * the body in DOM), not the autofocus input. Result: every keystroke
   * yanked focus off the input. See AgentsInventoryPageV2 Delete
   * dialog regression report. */
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKey = (e) => {
      const cb = onCloseRef.current;
      if (e.key === 'Escape' && cb) cb();
    };
    document.addEventListener('keydown', onKey);

    // Lock body scroll while open
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    /* Move initial focus into the dialog. Prefer the EXPLICIT
     * `autoFocus` target if the body declares one — comma-OR selectors
     * are evaluated in DOM order and the close-X button (rendered
     * above the body) would otherwise match first, defeating the
     * caller's intent. The close button is intentionally excluded
     * from the fallback set so confirm dialogs never accidentally
     * focus the dismiss control. */
    const id = window.setTimeout(() => {
      const root = dialogRef.current;
      if (!root) return;
      const target =
        root.querySelector('[autofocus]') ||
        root.querySelector('input,textarea,select,[tabindex]:not([tabindex="-1"])') ||
        root.querySelector('button:not([aria-label="Close dialog"])');
      if (target && typeof target.focus === 'function') target.focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(id);
    };
    // onClose intentionally NOT in deps — we deliberately want the
    // effect to re-run only when isOpen toggles, not when the parent
    // re-renders with a fresh onClose closure. (No disable directive
    // here because the project's ESLint config doesn't register
    // react-hooks/exhaustive-deps; a rule-specific disable comment
    // crashes the production build with "rule not found".)
  }, [isOpen]);

  if (!isOpen) return null;

  const widthClass = {
    sm:   'max-w-[420px]',
    md:   'max-w-[560px]',
    lg:   'max-w-[820px]',
    full: 'max-w-[90vw]',
  }[size];

  const resolvedAriaLabel =
    ariaLabel || (typeof title === 'string' ? title : undefined);

  return (
    <div className="fixed inset-0 z-50">
      <div
        aria-hidden="true"
        onClick={dismissOnBackdrop ? onClose : undefined}
        className="absolute inset-0"
        style={{ background: 'rgba(15, 23, 42, .35)' }}
      />
      <div className="absolute inset-0 flex items-center justify-center p-6 pointer-events-none">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={resolvedAriaLabel}
          className={
            'pointer-events-auto bg-v2-surface rounded-v2-lg shadow-v2-modal ' +
            'flex flex-col w-full max-h-[90vh] ' + widthClass
          }
        >
          {(title || onClose) && (
            <div className="flex items-center gap-3 px-5 py-4 border-b border-v2-border-subtle flex-shrink-0">
              {title && (
                <h2 className="text-[16px] font-semibold tracking-tight text-v2-ink m-0 flex-1">
                  {title}
                </h2>
              )}
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close dialog"
                  className={
                    'v2-focus-ring w-7 h-7 rounded-v2-sm ' +
                    'flex items-center justify-center text-v2-ink-muted ' +
                    'hover:bg-v2-muted hover:text-v2-ink ' +
                    'transition-colors duration-v2 ease-v2'
                  }
                >
                  <X size={16} aria-hidden="true" />
                </button>
              )}
            </div>
          )}
          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && (
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-v2-border-subtle flex-shrink-0">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Modal;
